import type { ToolResult } from "~/helpers/aiChat/chatStorage";

import { Box, Collapse, Icon } from "@mui/material";
import { useState } from "react";

import { BodyTextSmall, CaptionText } from "~/components/Typographies";
import humanizeToolName from "~/helpers/aiChat/humanizeToolName";

type ActivityState = "completed" | "failed" | "requested";

const stateFor = (status: ToolResult["status"]): ActivityState => {
	if (status === "ERROR") return "failed";
	if (status === "SUCCESS") return "completed";
	return "requested";
};

const STATE_ICON: Record<ActivityState, string> = {
	completed: "check_circle",
	failed: "error",
	requested: "pending"
};

const STATE_COLOR: Record<ActivityState, string> = {
	completed: "success.main",
	failed: "error.main",
	requested: "text.disabled"
};

const STATE_LABEL: Record<ActivityState, string> = {
	completed: "Completed",
	failed: "Failed",
	requested: "Requested"
};

interface FernieActivityRowProps {
	result: ToolResult;
}

const FernieActivityRow = ({ result }: FernieActivityRowProps) => {
	const [detailOpen, setDetailOpen] = useState(false);
	const state = stateFor(result.status);
	const detail = result.detail?.trim();
	const summary = result.summary?.trim();
	const label = summary || (state === "failed" ? STATE_LABEL.failed : humanizeToolName(result.toolName));
	const expandable = Boolean(detail);

	return (
		<Box>
			<Box
				aria-expanded={expandable ? detailOpen : undefined}
				onClick={expandable ? () => setDetailOpen((open) => !open) : undefined}
				role={expandable ? "button" : undefined}
				sx={{
					"&:hover": expandable ? { backgroundColor: "action.hover" } : undefined,
					alignItems: "center",
					borderRadius: "8px",
					cursor: expandable ? "pointer" : "default",
					display: "flex",
					gap: 0.75,
					px: 0.75,
					py: 0.5
				}}
			>
				<Icon aria-label={STATE_LABEL[state]} sx={{ color: STATE_COLOR[state], fontSize: 16 }}>
					{STATE_ICON[state]}
				</Icon>
				<BodyTextSmall
					sx={{
						color: state === "failed" ? "error.main" : "text.secondary",
						flex: 1,
						fontSize: "0.78rem",
						overflow: "hidden",
						textOverflow: "ellipsis",
						whiteSpace: "nowrap"
					}}
				>
					{label}
				</BodyTextSmall>
				{expandable && (
					<Icon sx={{ color: "text.disabled", fontSize: 16 }}>{detailOpen ? "expand_less" : "expand_more"}</Icon>
				)}
			</Box>
			{expandable && (
				<Collapse in={detailOpen} unmountOnExit>
					<Box
						component="pre"
						sx={{
							backgroundColor: "action.hover",
							borderRadius: "8px",
							color: "text.secondary",
							fontFamily: "'JetBrains Mono', monospace",
							fontSize: "0.68rem",
							lineHeight: 1.5,
							m: 0,
							maxHeight: 220,
							mt: 0.5,
							mx: 0.75,
							overflow: "auto",
							p: 1.25,
							whiteSpace: "pre-wrap",
							wordBreak: "break-word"
						}}
					>
						{detail}
					</Box>
				</Collapse>
			)}
		</Box>
	);
};

interface FernieActivityGroupProps {
	results: ToolResult[];
}

const FernieActivityGroup = ({ results }: FernieActivityGroupProps) => {
	if (results.length === 0) return null;

	return (
		<Box
			sx={{
				border: "1px solid",
				borderColor: "divider",
				borderRadius: "12px",
				display: "flex",
				flexDirection: "column",
				gap: 0.25,
				mt: 1.5,
				px: 0.75,
				py: 0.75
			}}
		>
			<CaptionText
				sx={{
					color: "text.disabled",
					fontSize: "0.6rem",
					fontWeight: 700,
					letterSpacing: "0.08em",
					pb: 0.25,
					pl: 0.75,
					textTransform: "uppercase"
				}}
			>
				Activity
			</CaptionText>
			{results.map((result) => (
				<FernieActivityRow key={result.toolName} result={result} />
			))}
		</Box>
	);
};

export default FernieActivityGroup;
